import { memo, useCallback, useMemo } from "react";
import { Platform } from "react-native";
import { MarkerClusterer as Clusterer } from "@googlemaps/markerclusterer";
import { MarkerF as WebMarker } from "@react-google-maps/api";
import { Marker as MobileMarker } from "react-native-maps";

import useThemeName from "../../../hooks/useThemeName";
import { Mappable } from "./types";

export type IsomorphicMarkerProps<T extends Mappable> = {
  item: T;
  onClick: (item: T) => void;
  isSelected: boolean;
  clusterer?: Clusterer;
  color: string;

  // HACK: react-native-map-cluster checks for the presence of this prop, so
  // we have to include it, even though we can calculate it from the
  // business.
  coordinate?: {
    latitude: number;
    longitude: number;
  };
};

type AppMarkerProps = {
  opacity: number;
  color: string;
  onClick: () => void;
  isSelected: boolean;
  coordinate: {
    latitude: number;
    longitude: number;
  };
  clusterer?: Clusterer;
};

const markerSVGPath =
  // eslint-disable-next-line max-len
  "M24.36,9.25C23.08,3.22,16.78-.85,10.29.15c-2.62.41-4.77,1.46-6.71,3.29C1.24,5.64.16,7.92.02,10.96c-.15,3.32.58,5.05,4.74,11.21,2.57,3.81,2.73,4.06,3.61,5.61,1.54,2.7,2.67,5.39,3.32,7.89.21.79.41,1.53.45,1.63.14.37.33-.04.78-1.69,1.19-4.26,2.77-7.32,6.94-13.47,2.31-3.4,3.95-6.32,4.36-7.76.37-1.31.43-3.73.14-5.13ZM12.27,16.89c-2.39,0-4.32-1.94-4.32-4.33s1.93-4.32,4.32-4.32,4.33,1.93,4.33,4.32-1.94,4.33-4.33,4.33Z";

const WebAppMarker = memo(function WebAppMarker({
  color,
  opacity,
  onClick,
  clusterer,
  isSelected,
  coordinate,
}: AppMarkerProps) {
  const themeName = useThemeName();
  const animation = isSelected ? google.maps.Animation.BOUNCE : undefined;

  const position = useMemo(
    () => ({
      lat: coordinate.latitude,
      lng: coordinate.longitude,
    }),
    [coordinate.latitude, coordinate.longitude]
  );

  const mapIcon = useMemo(
    () => ({
      path: markerSVGPath,
      fillColor: color,
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: themeName === "dark" ? "white" : "black",
      rotation: 0,
      scale: 1,
      // these both need anchors
      anchor: new google.maps.Point(14, 40),
      size: new google.maps.Size(28, 40),
    }),
    [color, themeName]
  );

  return (
    <WebMarker
      position={position}
      clusterer={clusterer}
      icon={mapIcon}
      onClick={onClick}
      opacity={opacity}
      noClustererRedraw={true}
      animation={animation}
    />
  );
});

const MobileAppMarker = ({ color, opacity, onClick, coordinate }: AppMarkerProps) => (
  <MobileMarker
    coordinate={coordinate}
    opacity={opacity}
    onPress={onClick}
    pinColor={color}
    flat
  />
);

const IsomorphicMarker = <T extends Mappable>({
  item,
  isSelected,
  onClick,
  clusterer,
  color,
  coordinate = {
    latitude: Number(item.latitude),
    longitude: Number(item.longitude),
  },
}: IsomorphicMarkerProps<T>) => {
  const handleClick = useCallback(() => {
    onClick(item);
  }, [onClick, item]);

  const opacity = isSelected ? 1 : 0.5;

  if (Platform.OS === "web") {
    return (
      <WebAppMarker
        color={color}
        opacity={opacity}
        onClick={handleClick}
        clusterer={clusterer}
        isSelected={isSelected}
        coordinate={coordinate}
      />
    );
  }

  return (
    <MobileAppMarker
      key={item.id}
      color={color}
      opacity={opacity}
      onClick={handleClick}
      isSelected={isSelected}
      coordinate={coordinate}
    />
  );
};

export default IsomorphicMarker;
